import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  MapPinIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  UserIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { 
  getRoutes, 
  createRoute, 
  deleteRoute,
  getRouteStats,
  type TransportRoute,
  type CreateRouteRequest,
  type RouteStop,
  type RouteStats
} from '@/api/routesApi'
import { getVehicles, type Vehicle } from '@/api/fleetApi'
import { getUsers, type User } from '@/api/usersApi'
import { institutionsApi, type Institution } from '@/api/institutionsApi'
import toast from 'react-hot-toast'

// Form schema for creating/editing routes
const routeSchema = z.object({
  routeName: z.string().min(3, 'Route name must be at least 3 characters'),
  routeNumber: z.string().min(1, 'Route number is required'),
  description: z.string().optional(),
  institutionId: z.string().optional(), // For super admin
})

type RouteFormData = z.infer<typeof routeSchema>

const RoutePlanningPage: React.FC = () => {
  const { user } = useAuth()
  const [routes, setRoutes] = useState<TransportRoute[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [driverFilter, setDriverFilter] = useState<string>('all')
  const [vehicleFilter, setVehicleFilter] = useState<string>('all')
  const [institutionFilter, setInstitutionFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedRoute, setSelectedRoute] = useState<TransportRoute | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRoutes, setTotalRoutes] = useState(0)
  const [pageSize] = useState(10)
  
  // Available data
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [drivers, setDrivers] = useState<User[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [stats, setStats] = useState<RouteStats>({
    totalRoutes: 0,
    activeRoutes: 0,
    routesWithVehicles: 0,
    routesWithDrivers: 0,
    routesWithoutVehicles: 0,
    routesWithoutDrivers: 0,
    totalStops: 0,
    averageStopsPerRoute: 0
  })

  // Stops management for modal
  const [routeStops, setRouteStops] = useState<RouteStop[]>([])
  const [operatingDays, setOperatingDays] = useState<string[]>([])
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([])
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>([])
  const [scheduleData, setScheduleData] = useState({
    departureTime: '07:00',
    departureLocation: 'Depot',
    arrivalTime: '08:00',
    arrivalLocation: 'School'
  })

  const routeForm = useForm<RouteFormData>({
    resolver: zodResolver(routeSchema),
    defaultValues: {
      routeName: '',
      routeNumber: '',
      description: '',
      institutionId: '',
    }
  })

  useEffect(() => {
    fetchRoutes()
    fetchVehicles()
    fetchDrivers()
    fetchStats()
    if (user?.role === 'SUPER_ADMIN') {
      fetchInstitutions()
    }
  }, [currentPage, searchQuery, statusFilter, driverFilter, vehicleFilter, institutionFilter])

  const fetchRoutes = async () => {
    setLoading(true)
    try {
      const response = await getRoutes({
        page: currentPage,
        limit: pageSize,
        search: searchQuery.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        assignedDriverId: driverFilter === 'all' ? undefined : driverFilter,
        assignedVehicleId: vehicleFilter === 'all' ? undefined : vehicleFilter,
        institutionId: user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' ? institutionFilter : undefined
      })
      
      setRoutes(response.data?.routes || [])
      setTotalPages(response.data?.pagination?.totalPages || 1)
      setTotalRoutes(response.data?.pagination?.totalRoutes || 0)
    } catch (error) {
      console.error('Failed to fetch routes:', error)
      toast.error('Failed to load routes')
    } finally {
      setLoading(false)
    }
  }

  const fetchVehicles = async () => {
    try {
      const params = user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' 
        ? { institutionId: institutionFilter, status: 'active' }
        : { status: 'active' }
        
      const response = await getVehicles(params)
      setVehicles(response.data?.vehicles || [])
    } catch (error) {
      console.error('Failed to fetch vehicles:', error)
    }
  }

  const fetchDrivers = async () => {
    try {
      const params = user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' 
        ? { institutionId: institutionFilter }
        : {}
        
      const response = await getUsers(params)
      const driversData = response.users?.filter((user: User) => 
        user.role?.toLowerCase().includes('driver')
      ) || []
      
      setDrivers(driversData)
    } catch (error) {
      console.error('Failed to fetch drivers:', error)
    }
  }

  const fetchInstitutions = async () => {
    try {
      const response = await institutionsApi.getInstitutions()
      setInstitutions(response || [])
    } catch (error) {
      console.error('Failed to fetch institutions:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const statsInstitutionId = user?.role === 'SUPER_ADMIN' && institutionFilter !== 'all' 
        ? institutionFilter 
        : undefined
        
      const response = await getRouteStats(statsInstitutionId)
      setStats(response.data || {})
    } catch (error) {
      console.error('Failed to fetch route stats:', error)
    }
  }

  const handleCreateRoute = async (data: RouteFormData) => {
    try {
      if (routeStops.length === 0) {
        toast.error('Please add at least one stop to the route')
        return
      }

      if (operatingDays.length === 0) {
        toast.error('Please select at least one operating day')
        return
      }

      const routeData: CreateRouteRequest = {
        ...data,
        assignedVehicleIds: selectedVehicles,
        assignedDriverIds: selectedDrivers,
        stops: routeStops,
        schedule: {
          departure: { time: scheduleData.departureTime, location: scheduleData.departureLocation },
          arrival: { time: scheduleData.arrivalTime, location: scheduleData.arrivalLocation }
        },
        operatingDays: operatingDays as any
      }

      if (user?.role === 'SUPER_ADMIN' && data.institutionId) {
        routeData.institutionId = data.institutionId
      }

      await createRoute(routeData)
      toast.success('Route created successfully!')
      setShowAddModal(false)
      routeForm.reset()
      setRouteStops([])
      setOperatingDays([])
      setSelectedVehicles([])
      setSelectedDrivers([])
      setScheduleData({
        departureTime: '07:00',
        departureLocation: 'Depot',
        arrivalTime: '08:00',
        arrivalLocation: 'School'
      })
      fetchRoutes()
      fetchStats()
    } catch (error: any) {
      console.error('Failed to create route:', error)
      const message = error?.response?.data?.message || 'Failed to create route'
      toast.error(message)
    }
  }

  const addStop = () => {
    const newStop: RouteStop = {
      stopId: `stop_${routeStops.length + 1}`,
      name: '',
      address: '',
      latitude: 0,
      longitude: 0,
      estimatedTime: '07:30',
      students: [],
      order: routeStops.length + 1
    }
    setRouteStops([...routeStops, newStop])
  }

  const removeStop = (index: number) => {
    const updatedStops = routeStops.filter((_, i) => i !== index)
    setRouteStops(updatedStops)
  }

  const updateStop = (index: number, field: keyof RouteStop, value: any) => {
    const updatedStops = [...routeStops]
    updatedStops[index] = { ...updatedStops[index], [field]: value }
    setRouteStops(updatedStops)
  }

  const handleDeleteRoute = async (route: TransportRoute) => {
    if (confirm(`Are you sure you want to delete route "${route.routeName}"?`)) {
      try {
        await deleteRoute(route.id)
        toast.success('Route deleted successfully!')
        fetchRoutes()
        fetchStats()
      } catch (error: any) {
        const message = error?.response?.data?.message || 'Failed to delete route'
        toast.error(message)
      }
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <MapPinIcon className="h-8 w-8 text-blue-600 mr-3" />
            Route Planning
          </h1>
          <p className="text-gray-600">Create and manage transportation routes for trips</p>
        </div>
        <div className="mt-4 lg:mt-0 flex space-x-3">
          <Button
            onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
            variant="secondary"
            size="sm"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            {viewMode === 'grid' ? 'Table View' : 'Grid View'}
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Route
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <MapPinIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Routes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalRoutes}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <TruckIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">With Vehicles</p>
              <p className="text-2xl font-bold text-gray-900">{stats.routesWithVehicles}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <UserIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">With Drivers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.routesWithDrivers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Stops</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalStops}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search routes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active Routes</option>
            <option value="inactive">Inactive Routes</option>
          </select>

          <select
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Drivers</option>
            <option value="">Unassigned</option>
            {drivers.map(driver => (
              <option key={driver.id || driver._id} value={driver.id || driver._id}>
                {driver.firstName} {driver.lastName}
              </option>
            ))}
          </select>

          <select
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Vehicles</option>
            <option value="">Unassigned</option>
            {vehicles.map(vehicle => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.vehicleNumber}
              </option>
            ))}
          </select>

          {user?.role === 'SUPER_ADMIN' && (
            <select
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Institutions</option>
              {institutions.map(institution => (
                <option key={institution.id} value={institution.id}>
                  {institution.name}
                </option>
              ))}
            </select>
          )}

          <Button
            onClick={() => {
              setSearchQuery('')
              setStatusFilter('all')
              setDriverFilter('all')
              setVehicleFilter('all')
              setInstitutionFilter('all')
              setCurrentPage(1)
            }}
            variant="secondary"
            size="sm"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Routes List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : routes.length === 0 ? (
        <div className="text-center py-12">
          <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No routes</h3>
          <p className="mt-1 text-sm text-gray-500">Create your first transportation route.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {routes.map((route) => (
            <motion.div
              key={route.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow border hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{route.routeName}</h3>
                    <p className="text-sm text-gray-600">{route.routeNumber}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    route.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {route.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {route.description && (
                  <p className="text-sm text-gray-600 mb-4">{route.description}</p>
                )}

                <div className="space-y-3">
                  {/* Route Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Stops:</span>
                      <span className="font-medium">{route.stopCount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Days:</span>
                      <span className="font-medium">{route.operatingDays?.length || 0}/7</span>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                    <div className="flex justify-between items-center">
                      <span>{route.schedule?.departure?.time} from {route.schedule?.departure?.location}</span>
                      <span>→</span>
                      <span>{route.schedule?.arrival?.time} to {route.schedule?.arrival?.location}</span>
                    </div>
                  </div>

                  {/* Assignments */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-center p-2 bg-blue-50 rounded">
                      <div className="text-lg font-bold text-blue-600">{route.assignedVehicles?.length || 0}</div>
                      <div className="text-xs text-gray-600">Vehicles</div>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                      <div className="text-lg font-bold text-green-600">{route.assignedDrivers?.length || 0}</div>
                      <div className="text-xs text-gray-600">Drivers</div>
                    </div>
                  </div>

                  {/* Vehicle List */}
                  {route.assignedVehicles && route.assignedVehicles.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Assigned Fleet:</p>
                      <div className="flex flex-wrap gap-1">
                        {route.assignedVehicles.slice(0, 3).map(vehicle => (
                          <span key={vehicle.id} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                            {vehicle.vehicleNumber}
                          </span>
                        ))}
                        {route.assignedVehicles.length > 3 && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                            +{route.assignedVehicles.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Driver List */}
                  {route.assignedDrivers && route.assignedDrivers.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">Assigned Drivers:</p>
                      <div className="flex flex-wrap gap-1">
                        {route.assignedDrivers.slice(0, 2).map(driver => (
                          <span key={driver.id} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                            {driver.firstName} {driver.lastName}
                          </span>
                        ))}
                        {route.assignedDrivers.length > 2 && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                            +{route.assignedDrivers.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setSelectedRoute(route)
                      // TODO: Open route details modal
                    }}
                    className="text-blue-600 hover:text-blue-900"
                    title="Edit Route"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteRoute(route)}
                    className="text-red-600 hover:text-red-900"
                    title="Delete Route"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        // Table View
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Route</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fleet Assignment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stops</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {routes.map((route) => (
                  <tr key={route.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{route.routeName}</div>
                        <div className="text-sm text-gray-500">{route.routeNumber}</div>
                        {route.description && (
                          <div className="text-xs text-gray-400 mt-1">{route.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium text-gray-900">
                          {route.schedule?.departure?.time} → {route.schedule?.arrival?.time}
                        </div>
                        <div className="text-xs text-gray-500">
                          {route.schedule?.departure?.location} to {route.schedule?.arrival?.location}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {route.operatingDays?.length || 0} days/week
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <TruckIcon className="h-3 w-3 text-blue-600 mr-1" />
                          <span className="text-sm text-gray-900">{route.assignedVehicles?.length || 0} vehicles</span>
                        </div>
                        <div className="flex items-center">
                          <UserIcon className="h-3 w-3 text-green-600 mr-1" />
                          <span className="text-sm text-gray-900">{route.assignedDrivers?.length || 0} drivers</span>
                        </div>
                        {route.assignedVehicles && route.assignedVehicles.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {route.assignedVehicles.slice(0, 2).map(vehicle => (
                              <span key={vehicle.id} className="px-1 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                                {vehicle.vehicleNumber}
                              </span>
                            ))}
                            {route.assignedVehicles.length > 2 && (
                              <span className="px-1 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                +{route.assignedVehicles.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{route.stopCount || 0} stops</div>
                      {route.stops && route.stops.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {route.stops[0]?.name}
                          {route.stops.length > 1 && (
                            <span> → {route.stops[route.stops.length - 1]?.name}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        route.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {route.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedRoute(route)
                            // TODO: Open route details modal
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Route"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRoute(route)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Route"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalRoutes)}</span> of{' '}
              <span className="font-medium">{totalRoutes}</span> routes
            </p>
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="secondary"
              size="sm"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              variant="secondary"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add Route Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center">
                  <MapPinIcon className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Create New Route</h3>
                    <p className="text-sm text-gray-600">Design a transportation route with stops</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    routeForm.reset()
                    setRouteStops([])
                    setOperatingDays([])
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={routeForm.handleSubmit(handleCreateRoute)} className="p-6 space-y-6">
                {/* Institution Selection */}
                {user?.role === 'SUPER_ADMIN' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-2" />
                      Institution
                    </h4>
                    <select
                      {...routeForm.register('institutionId')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select institution</option>
                      {institutions.map(institution => (
                        <option key={institution.id} value={institution.id}>
                          {institution.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    {...routeForm.register('routeName')}
                    label="Route Name"
                    placeholder="e.g., Downtown Route"
                    required
                  />
                  <Input
                    {...routeForm.register('routeNumber')}
                    label="Route Number"
                    placeholder="e.g., R001, Route A"
                    required
                  />
                </div>

                <Input
                  {...routeForm.register('description')}
                  label="Description"
                  placeholder="Optional route description"
                />

                {/* Schedule Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <ClockIcon className="h-5 w-5 text-purple-600 mr-2" />
                    Schedule
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Departure</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="time"
                          value={scheduleData.departureTime}
                          onChange={(e) => setScheduleData({...scheduleData, departureTime: e.target.value})}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Location"
                          value={scheduleData.departureLocation}
                          onChange={(e) => setScheduleData({...scheduleData, departureLocation: e.target.value})}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Arrival</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="time"
                          value={scheduleData.arrivalTime}
                          onChange={(e) => setScheduleData({...scheduleData, arrivalTime: e.target.value})}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Location"
                          value={scheduleData.arrivalLocation}
                          onChange={(e) => setScheduleData({...scheduleData, arrivalLocation: e.target.value})}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fleet Assignment */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <TruckIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Fleet Assignment
                  </h4>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Vehicles
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto bg-gray-50">
                      {vehicles.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No vehicles available</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {vehicles.map(vehicle => (
                            <label key={vehicle.id} className="flex items-center space-x-2 p-2 rounded hover:bg-white transition-colors">
                              <input
                                type="checkbox"
                                checked={selectedVehicles.includes(vehicle.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedVehicles([...selectedVehicles, vehicle.id])
                                  } else {
                                    setSelectedVehicles(selectedVehicles.filter(id => id !== vehicle.id))
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <span className="text-sm font-medium text-gray-700">{vehicle.vehicleNumber}</span>
                                <p className="text-xs text-gray-500">{vehicle.make} {vehicle.vehicleModel} ({vehicle.capacity} seats)</p>
                              </div>
                              <span className={`px-1 py-0.5 text-xs rounded ${
                                vehicle.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {vehicle.status}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 flex items-center">
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      {selectedVehicles.length} vehicle(s) selected
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Drivers
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">
                      {drivers.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No drivers available</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {drivers.map(driver => (
                            <label key={driver.id || driver._id} className="flex items-center space-x-2 p-2 rounded hover:bg-white transition-colors">
                              <input
                                type="checkbox"
                                checked={selectedDrivers.includes(driver.id || driver._id)}
                                onChange={(e) => {
                                  const driverId = driver.id || driver._id
                                  if (e.target.checked) {
                                    setSelectedDrivers([...selectedDrivers, driverId])
                                  } else {
                                    setSelectedDrivers(selectedDrivers.filter(id => id !== driverId))
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <span className="text-sm font-medium text-gray-700">
                                  {driver.firstName} {driver.lastName}
                                </span>
                                <p className="text-xs text-gray-500">{driver.email}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 flex items-center">
                      <UserIcon className="h-3 w-3 mr-1" />
                      {selectedDrivers.length} driver(s) selected
                    </p>
                  </div>
                </div>

                {/* Operating Days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Operating Days <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <label key={day} className="flex items-center space-x-1">
                        <input
                          type="checkbox"
                          checked={operatingDays.includes(day.toLowerCase())}
                          onChange={(e) => {
                            const dayLower = day.toLowerCase()
                            if (e.target.checked) {
                              setOperatingDays([...operatingDays, dayLower])
                            } else {
                              setOperatingDays(operatingDays.filter(d => d !== dayLower))
                            }
                          }}
                          className="rounded text-blue-600"
                        />
                        <span className="text-xs">{day.slice(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Route Stops */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Route Stops <span className="text-red-500">*</span>
                    </label>
                    <Button type="button" onClick={addStop} size="sm" className="bg-green-600 hover:bg-green-700">
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add Stop
                    </Button>
                  </div>
                  
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {routeStops.map((stop, index) => (
                      <div key={index} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Stop {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeStop(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <input
                            type="text"
                            placeholder="Stop name"
                            value={stop.name}
                            onChange={(e) => updateStop(index, 'name', e.target.value)}
                            className="px-2 py-1 border rounded text-sm"
                          />
                          <input
                            type="text"
                            placeholder="Address"
                            value={stop.address}
                            onChange={(e) => updateStop(index, 'address', e.target.value)}
                            className="px-2 py-1 border rounded text-sm"
                          />
                          <input
                            type="time"
                            value={stop.estimatedTime}
                            onChange={(e) => updateStop(index, 'estimatedTime', e.target.value)}
                            className="px-2 py-1 border rounded text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowAddModal(false)
                      routeForm.reset()
                      setRouteStops([])
                      setOperatingDays([])
                      setSelectedVehicles([])
                      setSelectedDrivers([])
                      setScheduleData({
                        departureTime: '07:00',
                        departureLocation: 'Depot',
                        arrivalTime: '08:00',
                        arrivalLocation: 'School'
                      })
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    Create Route
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default RoutePlanningPage